import { useState } from 'react';
import { Button, Choice, Csel, Icon, Popover, Slider, Switch, useToast } from '@efira/ui';
import { Link } from 'react-router';
import { SOUND_LIMITS } from '@efira/protocol';

import { useSetMixer, useSetPads, useSounds, useStopSounds } from '../../api/queries';
import { HOTKEYS } from '../hotkeys';
import { useMediaTab } from '../media';

/**
 * Звуковая треть шапки: вкладки, микшер, «Стоп», размер сетки.
 *
 * ВКЛАДКИ «GIF» И «МУЗЫКА» ПРИСУТСТВУЮТ, НО НЕ ОТКРЫВАЮТСЯ - и это видно.
 * Пустая панель, притворяющаяся готовой, хуже отключённой вкладки: первая
 * заставляет человека решать, сломалось ли, вторая отвечает сразу. Обе
 * приезжают единицей 5; «Музыка» и в конечном виде появляется только у игр
 * со своим саундтреком.
 */

/** Каналы, у которых СЕЙЧАС есть источник. Остальные приезжают с элементами. */
const LIVE_CHANNELS = [
  { id: 'master', title: 'Общая' },
  { id: 'pads', title: 'Пады' },
  { id: 'gameSounds', title: 'Звуки игры' },
  { id: 'gameMusic', title: 'Музыка игры' },
] as const;

export function SoundBar() {
  return (
    <>
      <SoundTabs />
      <div className="ms-auto flex shrink-0 items-center gap-1">
        <Mixer />
        <PanicButton />
        <PadsMenu />
      </div>
    </>
  );
}

/**
 * Три раздела медиа: звуки, гифки, музыка.
 *
 * Все три работают. Вкладка «Музыка» у эталона появляется только у игр со
 * своим саундтреком; у нас она про СВОЮ музыку стримера, поэтому видна
 * всегда - саундтрек игры приходит каналом «Музыка игры» в микшере.
 */
function SoundTabs() {
  const { tab, setTab } = useMediaTab();

  return (
    <Choice
      label="Разделы медиа"
      value={tab}
      onChange={setTab}
      size="sm"
      data-control-row="sound-tabs"
      /*
       * ЛЕСТНИЦА ДЕГРАДАЦИИ, а не сжатие. Сжимающаяся группа вкладок
       * обрезала «Музыку» на ширине трети 134 px - поймано воротами строк
       * управления. Ширина считается по ТРЕТИ, а не по окну.
       */
      className="shrink-0 @max-[330px]:hidden"
      /*
       * НАБОР ВАРИАНТОВ, А НЕ ТРИ ОТДЕЛЬНЫЕ КНОПКИ. Прежняя редакция
       * собирала их руками: `join` прежней библиотеки, `bg-border` на
       * выбранной и `btn-text` на остальных. Выбранное было видно только
       * потому, что цвет заливки подобран руками, - а ворота выбора
       * такую группу не видели вовсе: у неё `role="group"`.
       */
      options={[
        { value: 'sounds', label: 'Звуки', data: { 'data-sound-tab': 'sounds' } },
        { value: 'gif', label: 'GIF', data: { 'data-sound-tab': 'gif' } },
        { value: 'music', label: 'Музыка', data: { 'data-sound-tab': 'music' } },
      ]}
    />
  );
}

/**
 * Микшер: показываются каналы, у которых есть источник.
 *
 * Ползунок, ничем не управляющий, - это обещание управления. Каналы озвучки,
 * оповещений и эффектов подарков приезжают вместе со своими элементами
 * (единица 6), а в схеме микшера они объявлены уже сейчас, чтобы громкость,
 * выставленная заранее, не терялась.
 */
function Mixer() {
  const sounds = useSounds();
  const setMixer = useSetMixer();

  const mixer = sounds.data?.mixer ?? {};
  const master = mixer['master'] ?? { volume: 0.75, muted: false };
  const anyMuted = LIVE_CHANNELS.some((channel) => mixer[channel.id]?.muted === true);

  return (
    <Popover
      label="Микшер"
      className="w-80 p-3"
      placement="bottom-end"
      triggerClassName="gap-2"
      trigger={
        <span data-topbar="mixer-trigger" className="inline-flex items-center gap-2">
          {/* Значок меняется общим отключением: состояние звука видно из шапки. */}
          <Icon name={master.muted || anyMuted ? 'soundOff' : 'mixer'} size="sm" />
          <span className="@max-[520px]:hidden">Микшер</span>
        </span>
      }
    >
      <div data-mixer="panel" className="flex flex-col gap-3">
        {/* Подпись слева, тумблер прижат вправо и строка одна: так у эталона,
            и так шапка панели не съедает высоту, нужную каналам. */}
        <label className="flex items-center gap-2 text-xs whitespace-nowrap">
          <span className="text-foreground flex-1 text-sm font-semibold">Громкость</span>
          Выключить весь звук
          <Switch
            data-mixer="muteall"
            checked={master.muted}
            onCheckedChange={(next) => {
              setMixer.mutate({ channel: 'master', muted: next === true });
            }}
          />
        </label>

        {/* Одна строка, а не абзац: панель - орган управления, а не
            справка. Но вопрос «где остальные каналы» обязан иметь ответ.
            Строка ниже списка каналов. */}
        {LIVE_CHANNELS.map((channel) => (
          <MixerRow
            key={channel.id}
            channel={channel}
            state={mixer[channel.id] ?? { volume: 0.75, muted: false }}
            onMute={(muted) => {
              setMixer.mutate({ channel: channel.id, muted });
            }}
            onVolume={(volume) => {
              setMixer.mutate({ channel: channel.id, volume });
            }}
          />
        ))}

        <p className="text-foreground/80 text-sm">
          Озвучка, оповещения и эффекты подарков появятся здесь вместе с самими элементами.
        </p>
      </div>
    </Popover>
  );
}

/**
 * Строка канала микшера.
 *
 * ПОЛОЖЕНИЕ РУЧКИ - МЕСТНОЕ, СОХРАНЕНИЕ - ПО ОТПУСКАНИЮ. Ползунок был
 * привязан прямо к значению с сервера и сохранял каждый шаг: пока ответ
 * шёл, ручка отскакивала назад, и с клавиатуры громкость было не сдвинуть
 * вовсе - каждая стрелка возвращалась. Теперь тянется местное состояние, а
 * `onValueCommit` сохраняет один раз, когда человек отпустил.
 */
function MixerRow({
  channel,
  state,
  onMute,
  onVolume,
}: {
  channel: (typeof LIVE_CHANNELS)[number];
  state: { volume: number; muted: boolean };
  onMute: (muted: boolean) => void;
  onVolume: (volume: number) => void;
}) {
  const [dragged, setDragged] = useState<number | null>(null);

  /*
   * РУЧКА СТОИТ ТАМ, КУДА ЕЁ ПОСТАВИЛИ, ПОКА СЕРВЕР НЕ СОГЛАСИТСЯ.
   *
   * Прежняя редакция снимала местное значение сразу по отпусканию, и до
   * ответа сервера ручка отскакивала на прежнее место. При частых шагах
   * (стрелка с клавиатуры, автоповтор) это давало разъезд: на экране
   * 50 %, на сервере 55 % - следующий шаг считался уже от отскочившего
   * значения. Поймано сценарием микшера, который читает состояние с
   * сервера после перезагрузки.
   *
   * Сравнение идёт ПРИ ОТРИСОВКЕ, а не в эффекте: эффект, меняющий
   * состояние, запускает каскад перерисовок - правило проекта, и его же
   * сторожит `react-hooks/set-state-in-effect`.
   */
  const volume =
    dragged === null || Math.abs(state.volume - dragged) < 0.001 ? state.volume : dragged;

  return (
    /* ОДНА СТРОКА НА КАНАЛ, как у эталона: подпись, отключение,
               ползунок, проценты - на одной базовой линии. Двухэтажная
               строка вдвое снижает плотность, и семь каналов не помещаются
               в панель вовсе. */
    <div
      data-mixer-channel={channel.id}
      className="grid grid-cols-[6rem_1.5rem_1fr_3rem] items-center gap-2"
    >
      <span className="text-foreground min-w-0 truncate text-xs">{channel.title}</span>

      <Button
        variant="ghost"
        size="s"
        className="px-0"
        data-mixer-mute={channel.id}
        aria-pressed={state.muted}
        aria-label={`${state.muted ? 'Включить' : 'Отключить'} канал «${channel.title}»`}
        onClick={() => {
          onMute(!state.muted);
        }}
      >
        <Icon name={state.muted ? 'soundOff' : 'sound'} size="sm" />
      </Button>

      <Slider
        data-mixer-volume={channel.id}
        aria-label={`Громкость канала «${channel.title}»`}
        min={0}
        max={1}
        step={SOUND_LIMITS.volumeStep}
        value={[volume]}
        onValueChange={(next) => {
          setDragged(next[0] ?? 0);
        }}
        onValueCommit={(next) => {
          const value = next[0] ?? 0;

          setDragged(value);
          onVolume(value);
        }}
      />

      {/* Подпись В ПРОЦЕНТАХ: 0,75 человеку ничего не говорит. */}
      <span className="text-foreground/80 text-end text-xs whitespace-nowrap tabular-nums">
        {Math.round(volume * 100)} %
      </span>
    </div>
  );
}

/**
 * «Заглушить звук»: обрывает звук на оверлее немедленно и без подтверждения.
 *
 * Подтверждение здесь было бы издевательством: кнопка нужна ровно в тот
 * момент, когда в эфире звучит то, чего звучать не должно.
 *
 * НЕ «СТОП» И НЕ КРАСНАЯ (единица 22, находка владельца). Рядом в консоли
 * красная «Завершить сессию» - и две красные кнопки остановки с разным
 * смыслом, одна из которых подписана одним словом, читались как одно
 * действие. Эта - мгновенная и обратимая (следующий пад звучит снова),
 * поэтому вид у неё обычный, а подпись называет, что именно она делает.
 * Одно слово - одно действие: это сторожат ворота подписей
 * (`e2e/class-labels.spec.ts`).
 */
function PanicButton() {
  const stop = useStopSounds();
  const toast = useToast();

  return (
    <Button
      variant="secondary"
      size="s"
      className="gap-2"
      data-topbar="sound-panic"
      /* Второе имя - словарём реестра (`console.right.panic`): орган
         останавливает звук В ЭФИРЕ, и ворота требуют назвать его
         поимённо. У эталона он живёт в правой колонке, у нас - в полосе
         звука; пометка записывает это соответствие. */
      data-right="panic"
      /* Имя задано явно: на узкой трети подпись прячется, и без него у
         кнопки остаётся один значок - для чтеца это кнопка без имени. */
      aria-label={`Заглушить звук на оверлее, клавиша ${HOTKEYS.panic}`}
      title={`Заглушить звук на оверлее (клавиша ${HOTKEYS.panic})`}
      onClick={() => {
        stop.mutate(undefined, {
          onSuccess: () => {
            toast.show({ tone: 'success', title: 'Звук в эфире остановлен' });
          },
        });
      }}
    >
      <Icon name="soundOff" size="sm" />
      <span className="@max-[400px]:hidden">Заглушить звук</span>
    </Button>
  );
}

/** Размер сетки и вход в библиотеку. */
function PadsMenu() {
  const sounds = useSounds();
  const setPads = useSetPads();

  const limits = sounds.data?.limits ?? SOUND_LIMITS;
  const pads = sounds.data?.pads ?? SOUND_LIMITS.padsDefault;

  /* Размеры сетки - кратные четырём: столбцов четыре, и ряд, заполненный
     наполовину, выглядит как ошибка раскладки. */
  const options = [];

  for (let value = limits.padsMin; value <= limits.padsMax; value += 4) {
    options.push({ value: String(value), label: `${value} ячеек` });
  }

  return (
    <Popover
      label="Пады"
      className="w-64 p-3"
      placement="bottom-end"
      triggerClassName="gap-2"
      trigger={
        <span data-topbar="djpad-gear" className="inline-flex items-center gap-2">
          <Icon name="pads" size="sm" />
          <span className="@max-[520px]:hidden">Пады</span>
        </span>
      }
    >
      <div data-pads="menu" className="flex flex-col gap-3">
        <label data-pads="count-field" className="flex flex-col gap-1 text-xs">
          Ячеек на сетке
          <Csel
            value={String(pads)}
            aria-label="Ячеек на сетке"
            triggerClassName="w-full"
            options={options}
            onChange={(next) => {
              setPads.mutate(Number(next));
            }}
          />
        </label>

        {/* Предел объявлен ЧИСЛОМ, а не всплывает отказом. */}
        <p className="text-foreground/80 text-sm">
          От {limits.padsMin} до {limits.padsMax} ячеек. Своих звуков на аккаунт — до{' '}
          {limits.perAccount}, один файл — до {Math.round(limits.maxBytes / 1024 / 1024)} МБ.
        </p>

        <Link to="/app/settings/djpad" data-pads="library-link">
          Библиотека звуков
        </Link>
      </div>
    </Popover>
  );
}
